import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box} from '@mui/material';
import useDashboardWidgetData from '../../hooks/use-widget-data';
import useIntersectionObserver from '../../../../../../../hooks/use-intersection-observer';
import useCombinedRefs from '../../../../../../../hooks/use-combined-refs';
import {
    AugmentedDashboardWidgetEntry,
    DashboardEntry,
    DashboardViewFilterSectionFilters,
    DashboardWidgetEntry,
    DashboardWidgetLayoutConfig,
    DashboardWidgetType,
    SavedUiComponentStateWidgetConfig
} from "../../types";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import {DashboardWidgetConfig} from "../../dashboard-widget.config";
import {DefaultPaginationInfo} from "../../../../../../../../core/constants/enums";
import Utils from "../../../../../../../../core/services/utils/utils";
import EditDashboardWidgetTitleDialog, {EditDashboardWidgetTitleDialogProps} from "../../dialogs/edit-dashboard-widget-title";
import EditDashboardWidgetFiltersDialog, {EditDashboardWidgetFiltersDialogProps} from "../../dialogs/edit-dashboard-widget-filters";
import Api from "../../../../../../../../core/services/api/api";
import {DashboardWidgetVisualization} from "./visualization";
import {DashboardWidgetSettings} from "./settings";
import {DateRangeUtils} from "../../../../../../../components/app-specific/date-range-controller";

/**
 * Props for the DashboardWidget component
 */
interface DashboardWidgetProps {
    widget: AugmentedDashboardWidgetEntry;
    isEditMode?: boolean;
    dashboardId?: DashboardEntry['id'];
    filters: DashboardViewFilterSectionFilters;
    onFilterUpdated?: (id: DashboardWidgetEntry['connectionId'], filters?: DashboardViewFilterSectionFilters) => void;
    onTitleUpdated?: (id: DashboardWidgetEntry['connectionId'], title?: string) => void;
    onDelete?: (id: DashboardWidgetEntry['connectionId']) => void;
    cacheTimeout?: number; // in milliseconds
    savingDashboardWidgetConfig?: boolean;
    saveDashboardWidgetConfig?: (connectionId: DashboardWidgetEntry['connectionId'], config: DashboardWidgetLayoutConfig) => Promise<boolean>;
    saveWidgetOrderBy?: ((data: SavedUiComponentStateWidgetConfig) => Promise<void>);
    canViewWidgetFilters?: boolean;
    canManageWidgetFilters?: boolean;
    canExportWidgets?: boolean;
    simplified?: boolean;
}

const initEditTitleDialogState: EditDashboardWidgetTitleDialogProps = {
    open: false,
    title: undefined,
    onSave: undefined,
}

const initEditFiltersDialogState: EditDashboardWidgetFiltersDialogProps = {
    open: false,
    filters: undefined,
    onSave: undefined,
    widgetTitle: '',
    widgetParameters: [],
    widgetParameterTitles: {},
    forcedFilterRequirements: [],
    canEdit: false,
    mode: 'edit',
}

/**
 * DashboardWidget Component
 *
 * A container component for different types of dashboard widgets.
 * Handles widget refresh, caching, logging, and edit mode functionality.
 */
const DashboardWidget: React.FC<DashboardWidgetProps> = ({
                                                             filters,
                                                             onFilterUpdated,
                                                             onTitleUpdated,
                                                             isEditMode = false,
                                                             canViewWidgetFilters = false,
                                                             canManageWidgetFilters = false,
                                                             onDelete,
                                                             cacheTimeout,
                                                             dashboardId,
                                                             savingDashboardWidgetConfig,
                                                             saveDashboardWidgetConfig,
                                                             saveWidgetOrderBy,
                                                             widget,
                                                             simplified = false,
                                                             canExportWidgets = false,
                                                         }) => {
    const [viewingSettings, setViewingSettings] = useState(false);
    const [editTitleDialogState, setEditTitleDialogState] = useState<EditDashboardWidgetTitleDialogProps>(initEditTitleDialogState);
    const [editFiltersDialogState, setEditFiltersDialogState] = useState<EditDashboardWidgetFiltersDialogProps>(initEditFiltersDialogState);
    const isMounted = useIsMounted();

    const containerRef = useRef<HTMLElement>();
    const [intersectionObserverRef, isIntersecting] = useIntersectionObserver();
    const ref = useCombinedRefs(intersectionObserverRef, containerRef);
    const config = useMemo(() => new DashboardWidgetConfig(widget.id, widget.variant, widget.type), [widget.id, widget.variant, widget.type]);
    const hasPagination = useMemo(() => config.hasPagination, [config.hasPagination]);
    const hasNavigation = useMemo(() => config.hasNavigation, [config.hasNavigation]);

    /**
     * As soon as the widget is in edit mode, we hide the settings view
     */
    useEffect(() => {
        if (isEditMode) {
            setViewingSettings(false);
        }
    }, [isEditMode]);


    const mergedFilters = useMemo(() => {
        if (!widget?.filters) {
            return filters;
        }
        if (widget?.filters?.mergeWithParentFilters) {
            const merged = Utils.deepMerge({}, filters, widget?.filters) as DashboardViewFilterSectionFilters;
            // Resolve the effective date ranges using our adapter
            const widgetDateRange = DateRangeUtils.getDateRangeValueFromLegacyFilters(widget.filters);
            const parentDateRange = DateRangeUtils.getDateRangeValueFromLegacyFilters(filters);

            const unifiedValue = widgetDateRange || parentDateRange || undefined;
            merged.dateRangeValue = unifiedValue ? JSON.stringify(unifiedValue) : undefined;

            // Delete legacy fields to ensure components only see the unified dateRangeValue
            delete (merged as any).startDate;
            delete (merged as any).endDate;
            delete (merged as any).useDateRange;
            delete (merged as any).dateRangeType;
            delete (merged as any).dateRangeBatch;
            delete (merged as any).dateRangeCount;
            delete (merged as any).dateRangeUntilNow;
            delete (merged as any).dateRangeFromNow;
            return merged;
        } else {
            return widget?.filters;
        }

    }, [filters, widget?.filters, widget.id])

    /**
     * Saves the widget's Title.
     */
    const saveWidgetTitle = useCallback(async (title?: string) => {
        const response = await Api.updateCustomDashboardWidget({
            id: widget?.connectionId,
            customTitle: title ?? "",
            customFilter: widget?.customFilter ?? undefined,
        })
        if (!isMounted())
            return true;
        if (response.resultFlag) {
            onTitleUpdated?.(widget?.connectionId, title);
        }
        return response.resultFlag;
    }, [isMounted, onTitleUpdated, widget?.connectionId, widget?.customFilter]);

    /**
     * Saves the widget's Filters.
     */
    const saveWidgetFilters = useCallback(async (filters?: DashboardViewFilterSectionFilters) => {
        if (!filters && config.forcedFilterRequirements.length) {
            return false;
        }
        const response = await Api.updateCustomDashboardWidget({
            id: widget?.connectionId,
            customTitle: widget?.customTitle ?? undefined,
            customFilter: filters ? JSON.stringify(filters) : "",
        })
        if (!isMounted())
            return true;
        if (response.resultFlag) {
            onFilterUpdated?.(widget?.connectionId, filters);
        }
        return response.resultFlag;
    }, [isMounted, widget?.connectionId, widget?.customTitle, onFilterUpdated, config.forcedFilterRequirements.length]);


    const {
        data,
        isLoading,
        hasFetchError,
        refresh,
        lastFetched,
        paginationInfo,
        setPaginationInfo,
        orderBy,
        setOrderBy,
        exportData,
        isExporting,
    } = useDashboardWidgetData({
        widgetId: widget?.id,
        widgetTitle: widget?.customTitle ?? widget?.title,
        widgetVariant: widget?.variant,
        widgetConnectionId: widget?.connectionId,
        dashboardId: dashboardId,
        parameters: widget?.parameters,
        widgetType: widget?.type,
        filters: mergedFilters,
        cacheTimeout,
        isEnabled: isIntersecting,
        initialPagination: DefaultPaginationInfo,
        usePagination: hasPagination,
        widgetOrderBy: widget?.orderBy,
        saveWidgetOrderBy: saveWidgetOrderBy,
        useSorting: widget?.type !== DashboardWidgetType.infoBlock,
    });

    /**
     * Toggle the view between widget settings and visualization
     */
    const toggleViewSettings = useCallback(() => {
        setViewingSettings(prev => !prev);
    }, [setViewingSettings]);

    const hasNoConfig = useMemo(() => !widget?.configuration, [widget?.configuration]);
    const showLoadingIndicator = useMemo(() => isLoading && (widget?.type !== DashboardWidgetType.table || !hasNoConfig), [isLoading, widget?.type, hasNoConfig]);
    const canToggleViewSettings = useMemo(() => widget?.type !== DashboardWidgetType.infoBlock && !isEditMode, [widget?.type, isEditMode]);

    const noDataAvailableOrNotImplementedYet =
        (!isLoading && hasNoConfig) ||
        (!isLoading && !Object.values(DashboardWidgetType).includes(widget?.type));

    const containerMinHeight = useMemo(() => config.getContainerMinHeight(isLoading, simplified, noDataAvailableOrNotImplementedYet, hasFetchError), [config, isLoading, simplified, noDataAvailableOrNotImplementedYet, hasFetchError]);

    /**
     * Opens the dialog to edit the widget title
     */
    const openEditWidgetTitleDialog = useCallback(() => {
        setEditTitleDialogState({
            open: true,
            title: widget?.customTitle ?? widget?.title ?? "",
            onSave: saveWidgetTitle,
        });
    }, [saveWidgetTitle, widget?.customTitle, widget?.title]);

    /**
     * Opens the dialog to view or edit widget filters
     */
    const openEditWidgetFiltersDialog = useCallback(() => {
        setEditFiltersDialogState({
            open: true,
            widgetTitle: widget?.customTitle ?? widget?.title,
            widgetParameters: widget?.parameters.map(e => e.name),
            widgetParameterTitles: Object.fromEntries((widget?.parameters ?? []).map((parameter) => [parameter.name, parameter.title])),
            forcedFilterRequirements: config.forcedFilterRequirements,
            canEdit: canManageWidgetFilters,
            filters: widget?.filters,
            onSave: canManageWidgetFilters ? saveWidgetFilters : undefined,
            mode: 'edit',
        });
    }, [widget?.title, widget?.customTitle, widget?.parameters, widget?.filters, canManageWidgetFilters, saveWidgetFilters, config.forcedFilterRequirements]);

    return (
        <>
            <Box
                ref={ref}
                sx={{
                    height: '100%',
                    perspective: '1000px',
                    minHeight: containerMinHeight,
                }}
            >
                <Box
                    sx={{
                        border: '1px solid',
                        borderRadius: 1,
                        borderColor: 'divider',
                        backgroundColor: 'background.default',
                        height: '100%',
                        width: '100%',
                        position: 'relative',
                        transition: theme => theme.transitions.create(['transform', 'color', 'backgroundColor']),
                        transformStyle: 'preserve-3d',
                        ...(viewingSettings && {
                            transform: 'rotateY(180deg)',
                        }),
                    }}
                >
                    <Box sx={{
                        height: '100%',
                        width: '100%',
                        position: 'absolute',
                        backfaceVisibility: 'hidden',
                        ...(viewingSettings && {
                            "*": {
                                pointerEvents: 'none',
                            },
                        })
                    }}>
                        <DashboardWidgetVisualization
                            noDataAvailableOrNotImplementedYet={noDataAvailableOrNotImplementedYet}
                            widget={widget}
                            config={config}
                            lastFetched={lastFetched}
                            refresh={refresh}
                            isLoading={isLoading}
                            hasFetchError={hasFetchError}
                            onDelete={onDelete}
                            filters={filters}
                            isExporting={isExporting}
                            exportData={canExportWidgets ? exportData : undefined}
                            isEditMode={isEditMode}
                            data={data}
                            paginationInfo={paginationInfo}
                            setPaginationInfo={setPaginationInfo}
                            orderBy={orderBy}
                            setOrderBy={setOrderBy}
                            isIntersecting={isIntersecting}
                            toggleViewSettings={canToggleViewSettings ? toggleViewSettings : undefined}
                            hasPagination={hasPagination}
                            hasNavigation={hasNavigation}
                            openEditWidgetTitleDialog={openEditWidgetTitleDialog}
                            openEditWidgetFiltersDialog={openEditWidgetFiltersDialog}
                            canViewWidgetFilters={canViewWidgetFilters}
                            canManageWidgetFilters={canManageWidgetFilters}
                            simplified={simplified}
                            showLoadingIndicator={showLoadingIndicator}
                            hasNoConfig={hasNoConfig}
                        />
                    </Box>
                    <Box sx={{
                        height: '100%',
                        width: '100%',
                        position: 'absolute',
                        backfaceVisibility: 'hidden',
                        transform: 'rotateY(180deg)',
                        backgroundColor: 'background.default',
                        ...(!viewingSettings && {
                            "*": {
                                pointerEvents: 'none',
                            },
                        })
                    }}>
                        <DashboardWidgetSettings
                            widget={widget}
                            saveDashboardWidgetConfig={saveDashboardWidgetConfig}
                            savingDashboardWidgetConfig={savingDashboardWidgetConfig}
                            toggleViewSettings={toggleViewSettings}
                        />
                    </Box>
                </Box>
            </Box>
            <EditDashboardWidgetTitleDialog
                {...editTitleDialogState}
                onClose={() => setEditTitleDialogState(initEditTitleDialogState)}
            />
            <EditDashboardWidgetFiltersDialog
                {...editFiltersDialogState}
                onClose={() => setEditFiltersDialogState(initEditFiltersDialogState)}
            />

        </>
    );
};

export default DashboardWidget;